import apiClient from './client';

export type AlertRule = {
	id: string;
	user_id: string;
	type: string;
	symbol: string | null;
	threshold_usd: string;
	enabled: boolean;
	webhook_url: string | null;
	webhook_enabled: boolean;
	webhook_format: 'raw_json' | 'discord' | 'slack';
	created_at: string;
};

export type AlertEvent = {
	id: string;
	rule_id: string;
	user_id: string;
	triggered_at: string;
	message: string;
};

export type AlertDelivery = {
	id: string;
	event_id: string;
	rule_id: string;
	user_id: string;
	webhook_url: string;
	format: string;
	status: 'pending' | 'success' | 'failed';
	attempt_count: number;
	last_attempt_at: string | null;
	last_error: string | null;
	last_status_code: number | null;
	last_response_snippet: string | null;
	created_at: string;
	updated_at: string;
};

export async function createAlertRule(input: {
	userId: string;
	type: string;
	symbol?: string | null;
	thresholdUsd: number;
	enabled?: boolean;
	webhookUrl?: string | null;
	webhookEnabled?: boolean;
	webhookFormat?: 'raw_json' | 'discord' | 'slack';
}) {
	const res = await apiClient.post<{ rule: AlertRule }>(`/analytics/alerts/rules`, input);
	return res.data.rule;
}

export async function listAlertRules(userId: string) {
	const res = await apiClient.get<{ rules: AlertRule[] }>(`/analytics/alerts/rules/${userId}`);
	return res.data.rules;
}

export async function listAlertEvents(userId: string) {
	const res = await apiClient.get<{ events: AlertEvent[] }>(`/analytics/alerts/events/${userId}`);
	return res.data.events;
}

export async function listAlertDeliveries(userId: string) {
	const res = await apiClient.get<{ deliveries: AlertDelivery[] }>(`/analytics/alerts/deliveries/${userId}`);
	return res.data.deliveries;
}

export async function updateAlertRule(
	ruleId: string,
	patch: {
		thresholdUsd?: number;
		enabled?: boolean;
		webhookUrl?: string | null;
		webhookEnabled?: boolean;
		webhookFormat?: 'raw_json' | 'discord' | 'slack';
	}
) {
	const res = await apiClient.patch(`/analytics/alerts/rules/${ruleId}`, patch);
	return res.data;
}

export async function deleteAlertRule(ruleId: string) {
	const res = await apiClient.delete(`/analytics/alerts/rules/${ruleId}`);
	return res.data;
}

// ===== ALERT RULE ENGINE ENDPOINTS (Chunk 32) =====

export type AlertRuleV2 = {
	id: string;
	userId: string;
	name: string;
	type: 'price_change' | 'volatility_spike' | 'concentration_warning' | 'position_change' | 'loss_threshold' | 'gain_threshold' | 'defi_risk' | 'tax_loss_harvest';
	enabled: boolean;
	condition: {
		asset?: string;
		threshold: number;
		operator: '>' | '<' | '>=' | '<=' | '=' | '!=';
		lookbackPeriod?: number;
	};
	severity: 'info' | 'warning' | 'critical';
	createdAt: string;
	updatedAt: string;
};

export type AlertV2 = {
	id: string;
	userId: string;
	ruleId: string;
	type: string;
	severity: 'info' | 'warning' | 'critical';
	title: string;
	message: string;
	data: Record<string, any>;
	triggeredAt: string;
	readAt?: string;
};

export type NotificationPreferences = {
	userId: string;
	channels: {
		email: boolean;
		push: boolean;
		sms: boolean;
		webhook: boolean;
		in_app: boolean;
	};
	severityFilter: {
		info: boolean;
		warning: boolean;
		critical: boolean;
	};
	quiet_hours?: {
		enabled: boolean;
		start: string;
		end: string;
	};
	webhookUrl?: string;
	phoneNumber?: string;
	updatedAt: string;
};

export type NotificationEvent = {
	id: string;
	userId: string;
	alertId: string;
	deliveredTo: string[];
	status: 'sent' | 'pending' | 'failed' | 'filtered' | 'deferred';
	error?: string;
	deliveredAt: string;
};

// Alert Rule V2 API
export async function listAlertRulesV2(userId: string): Promise<AlertRuleV2[]> {
	const res = await apiClient.get<AlertRuleV2[] | { rules?: AlertRuleV2[] }>(
		`/analytics/alerts/rules/${userId}`,
	);
	const data = res.data as unknown;
	if (Array.isArray(data)) return data;
	if (data && typeof data === 'object' && Array.isArray((data as { rules?: unknown }).rules)) {
		return (data as { rules: AlertRuleV2[] }).rules;
	}
	return [];
}

export async function createAlertRuleV2(userId: string, rule: Omit<AlertRuleV2, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) {
	const res = await apiClient.post<AlertRuleV2>(`/analytics/alerts/rules/${userId}`, rule);
	return res.data;
}

export async function updateAlertRuleV2(userId: string, ruleId: string, updates: Partial<AlertRuleV2>) {
	const res = await apiClient.patch<AlertRuleV2>(`/analytics/alerts/rules/${ruleId}`, { userId, updates });
	return res.data;
}

export async function deleteAlertRuleV2(userId: string, ruleId: string) {
	const res = await apiClient.delete(`/analytics/alerts/rules/${ruleId}`, {
		data: { userId }
	});
	return res.data;
}

// Alert V2 API
export async function getAlertsV2(userId: string, limit: number = 50) {
	const res = await apiClient.get<AlertV2[]>(`/analytics/alerts/${userId}`, {
		params: { limit }
	});
	return res.data;
}

export async function markAlertAsReadV2(userId: string, alertId: string) {
	const res = await apiClient.post(`/analytics/alerts/${userId}/${alertId}/read`);
	return res.data;
}

export async function getUnreadAlertCountV2(userId: string) {
	const res = await apiClient.get<{ unreadCount: number }>(`/analytics/alerts/${userId}/unread/count`);
	return res.data.unreadCount;
}

// Notification Preferences API
export async function getNotificationPreferences(userId: string) {
	const res = await apiClient.get<NotificationPreferences>(`/analytics/notifications/preferences/${userId}`);
	return res.data;
}

export async function updateNotificationPreferences(userId: string, prefs: Partial<NotificationPreferences>) {
	const res = await apiClient.post<NotificationPreferences>(`/analytics/notifications/preferences/${userId}`, prefs);
	return res.data;
}

export async function testNotificationChannels(userId: string) {
	const res = await apiClient.post<Array<{ channel: string; success: boolean; error?: string }>>(`/analytics/notifications/test/${userId}`);
	return res.data;
}

export async function getNotificationHistory(userId: string, limit: number = 50) {
	const res = await apiClient.get<NotificationEvent[]>(`/analytics/notifications/history/${userId}`, {
		params: { limit }
	});
	return res.data;
}

// ===== ALERT HISTORY (Chunk 175) =====

export type AlertHistoryEntry = {
	id: string;
	alert_rule_id: string | null;
	user_id: string;
	trigger_type: string;
	trigger_value: Record<string, unknown>;
	message: string;
	fired_at: string;
	acknowledged_at: string | null;
	rule_name: string | null;
};

export async function getAlertHistory(userId: string, limit = 50): Promise<AlertHistoryEntry[]> {
	const res = await apiClient.get<{ history: AlertHistoryEntry[]; pagination: unknown }>(
		`/analytics/alert-history/${userId}`,
		{ params: { limit } }
	);
	return res.data.history;
}

export async function acknowledgeAlertHistory(alertId: string): Promise<{ id: string; acknowledged_at: string }> {
	const res = await apiClient.post<{ id: string; acknowledged_at: string }>(
		`/analytics/alert-history/${alertId}/acknowledge`
	);
	return res.data;
}